import { ReactNode } from 'react';

export interface ChangelogEntry {
  id: number;
  date: string;
  title: string;
  summary: string;
  content: ReactNode;
  tags?: ('feature' | 'improvement' | 'fix' | 'game')[];
  image?: { src: string; alt: string };
  video?: { src: string };
}

const changelogItems: ChangelogEntry[] = [
  {
    id: 40,
    date: '2026-03-27',
    title:
      'Improved auto saving for lesson plans, notes, word mat and custom levels.',
    summary:
      'Lesson plans, notes, word mats and custom levels now autosave as you work.',
    content:
      'Lesson plans, notes, word mats and custom levels now autosave as you work. This means you can work on these items without worrying about losing your progress.',
    tags: ['improvement'],
  },
  {
    id: 39,
    date: '2026-03-27',
    title: 'Mobile responsive dashboard',
    summary: 'The dashboard is now usable on mobile and tablet devices.',
    content:
      'The dashboard layout now adapts to smaller screens, making it easier to view learner data and manage your account on mobile and tablet devices.',
    tags: ['improvement'],
  },
  {
    id: 38,
    date: '2026-03-23',
    title: 'Custom game levels',
    summary:
      'You can now create custom game levels and add them to lesson plans.',
    content:
      'You can build custom game levels from scratch or generate them from the scope and sequence or from a lesson plan. This will make it easy to create personalised game levels for your learners.',
    tags: ['feature', 'game'],
  },
  {
    id: 37,
    date: '2026-03-19',
    title: 'Word Matrix activity block',
    summary:
      'A new Word Matrix activity block is available in the lesson planner.',
    content:
      'You can now add a Word Matrix activity block to your lesson plans, giving you another tool for exploring spelling patterns with your learners.',
    tags: ['feature'],
  },
  {
    id: 36,
    date: '2026-03-11',
    title: 'Activation checklist for new organisations',
    summary:
      'You can now see a guided activation checklist to help with getting started.',
    content:
      'You can now view a step-by-step activation checklist in the sidebar to guide you through setting up learners, exploring the game, using the lesson planner, reviewing learner data, and inviting team members. Click on the "Need Help?" button in the sidebar to revisit the checklist.',
    tags: ['feature'],
  },
  {
    id: 35,
    date: '2026-03-08',
    title: 'Sort learners by first or last name',
    summary:
      'The learner list can now be sorted by first name or last name, in ascending or descending order.',
    content:
      'You can now sort the learner list by name — sort by first name or last name, in A→Z or Z→A order. The default sort is by last name A→Z.',
    tags: ['feature'],
  },
  {
    id: 34,
    date: '2026-03-05',
    title: 'Smarter distractor cards in word building games',
    summary:
      'Distractor cards in minigames are now more challenging, using phonetically similar sounds.',
    content:
      'Distractor cards in Word Weaver, Mystic Match, and Phoneme Forge now prioritise graphemes that sound similar to those in the target word — including confusable sound pairs like voiced and voiceless consonants. This makes the games more challenging and better at reinforcing phoneme discrimination.',
    tags: ['improvement', 'game'],
  },
  {
    id: 33,
    date: '2026-03-04',
    title: 'New level maps for forest and spring realms',
    summary:
      'Six new tilemaps have been added to the forest and spring biomes.',
    content:
      'Six new level maps have been added across the forest and spring biomes, bringing more variety to the game world. The map selection system has also been improved to match maps to their correct biome.',
    tags: ['feature', 'game'],
  },
  {
    id: 32,
    date: '2026-03-04',
    title: 'Added anchoring words to sounds in Mystic Match.',
    summary:
      'Mystic Match now includes anchoring words so learners can hear the target phoneme within a word.',
    content:
      'Mystic Match now includes a button to hear the target phoneme within a word. This helps learners to discern the particular sound by hearing it in situ.',
    tags: ['feature', 'game'],
  },
  {
    id: 31,
    date: '2026-02-24',
    title: 'Added password visibility toggle to student login screen.',
    summary:
      'Learners can now toggle password visibility on the game sign-in screen.',
    content:
      'The Game login screen now includes a password visibility toggle so learners can check what they have typed.',
    tags: ['feature'],
  },
  {
    id: 30,
    date: '2026-02-24',
    title: 'Suggest features from your Spellcaster dashboard',
    summary:
      'A new feature requests board lets you share ideas and vote on suggestions directly from the dashboard.',
    content:
      'You can now submit feature requests and bug reports directly from the Spellcaster dashboard using our new feature requests board. Access it from the "Feature Requests" option in the sidebar to share your ideas, view suggestions from others and vote on ideas you want to see implemented.',
    tags: ['feature'],
  },
  {
    id: 29,
    date: '2026-02-24',
    title: 'Dashboard refresh',
    summary:
      'The dashboard sidebar and navigation have been reorganised for a cleaner experience.',
    content:
      "The dashboard layout has been refreshed. Key actions like What's New, Give Feedback, Need Help, and Invite Team Members have moved into the sidebar footer for easier access. The What's New panel allows you to preview Spellcaster's latest improvements from the dashboard.",
    tags: ['improvement'],
  },
  {
    id: 28,
    date: '2026-02-24',
    title: 'Simpler logins & bulk password management',
    summary:
      'Autogenerated usernames and passwords are now simpler, and you can update passwords and usernames in bulk.',
    content:
      'Generated usernames and passwords for learners are now shorter and easier to type. You can regenerate passwords with one click while creating learners, and update passwords and usernames for multiple learners at once by clicking the checkboxes besides learner names.',
    tags: ['feature'],
  },
  {
    id: 27,
    date: '2026-02-24',
    title: 'Password visibility toggle',
    summary:
      'Learners can now toggle password visibility on the game sign-in screen.',
    content:
      'The Game login screen now includes a password visibility toggle so learners can check what they have typed.',
    tags: ['feature'],
  },
  {
    id: 26,
    date: '2026-02-18',
    title: 'Bulk settings management',
    summary: 'Change game settings for multiple learners at once.',
    content:
      'You can now select multiple learners and change their game settings in one go. Select learners from the learner list or group view via the checkboxes beside learner names, click "Change Settings", and apply the changes to all selected learners at once.',
    tags: ['feature'],
  },
  {
    id: 25,
    date: '2026-02-17',
    title: 'Text editor toolbar upgraded',
    summary:
      'The text editor toolbar now supports additional formatting, list styles, and color highlighting.',
    content:
      'The text editor toolbar has been upgraded with more formatting and styling options, including font colours and highlighting. You can now use more flexible lists and nested sub-lists. Session summary generation from the activity feed also takes advantage of these new formatting tools for richer, clearer summaries.',
    tags: ['improvement'],
  },
  {
    id: 21,
    date: '2026-02-12',
    title: 'Scope & sequence view improvements',
    summary: 'Improved the layout of the Spellcaster Scope & Sequence.',
    content:
      'Improved the layout of the Spellcaster Scope & Sequence to make it more user-friendly and easier to search.',
    tags: ['improvement'],
  },
  {
    id: 24,
    date: '2026-02-11',
    title: 'Player guidance improvements',
    summary:
      'Enhanced in-game prompts to give learners clearer guidance while playing.',
    content:
      'The player experience now features improved on-screen guidance, making it easier for learners to understand what to do next during gameplay. This helps learners stay on track and get the most out of each session.',
    tags: ['improvement'],
  },
  {
    id: 23,
    date: '2026-02-11',
    title: 'Add learner notes feature for learner profiles',
    summary:
      'Add a new feature to the learner profile page to allow teachers to add notes to learners.',
    content:
      'Added a new feature to the learner profile page to allow teachers to add notes to learners and generate summaries of game sessions directly from the activity feed.',
    tags: ['feature'],
  },
  {
    id: 20,
    date: '2026-02-11',
    title: 'Bulk add learners with passwords',
    summary:
      'You can now include passwords when bulk adding learners via CSV upload.',
    content:
      'The CSV bulk import now supports a password column, so you can set passwords for learners during import rather than having to edit them individually afterwards.',
    tags: ['feature'],
  },
  {
    id: 19,
    date: '2026-02-10',
    title: 'Insights multi-select fix',
    summary: 'Fixed an issue with multi-select filters in Insights.',
    content:
      'Resolved a bug where selecting multiple filters in the Insights view would not apply correctly, causing unexpected results.',
    tags: ['fix'],
  },
  {
    id: 18,
    date: '2026-02-07',
    title: 'Lesson Plan Presenter mode',
    summary:
      'Present lesson plans directly from the dashboard in a fullscreen view.',
    content:
      'A new presenter mode lets you display lesson plans in a clean, fullscreen layout — perfect for projecting in the classroom. Access it from the lesson plan editor toolbar.',
    tags: ['feature'],
  },
  {
    id: 17,
    date: '2026-02-05',
    title: 'New character cosmetics',
    summary: 'Fresh cosmetic items are available for learner avatars.',
    content:
      'A new batch of cosmetic items has been added to the in-game store, giving learners more ways to personalise their characters.',
    tags: ['game'],
  },
  {
    id: 16,
    date: '2026-02-03',
    title: 'Unified lesson plan & worksheet experience',
    summary:
      'Lesson plans and worksheets now share a streamlined editing experience.',
    content:
      'The worksheet editor has been unified with the lesson plan editor, providing a more consistent experience when creating teaching materials. Worksheets are now accessible directly from within lesson plans.',
    tags: ['improvement'],
  },
  {
    id: 14,
    date: '2026-01-27',
    title: 'Minigame bug fixes',
    summary: 'Fixed issues with certain in-game minigames.',
    content:
      'Resolved several issues affecting minigame stability, including problems with input handling and scoring in certain game modes.',
    tags: ['fix', 'game'],
  },
  {
    id: 13,
    date: '2026-01-22',
    title: 'Lesson plan persistence',
    summary: 'Lesson plans now auto-save as you edit.',
    content:
      'Lesson plans can now be saved as you work. You can manage saved lesson plans in the Lesson Plans page.',
    tags: ['feature'],
  },
  {
    id: 12,
    date: '2026-01-20',
    title: 'Word mat persistence & editor improvements',
    summary: 'Word mats now save automatically with an improved editor.',
    content:
      'Like lesson plans, word mats now can be saved as you edit. The editor has also been polished with better controls and a smoother workflow.',
    tags: ['feature'],
  },
  {
    id: 11,
    date: '2026-01-15',
    title: 'Duel mode riposte clarity and word repeat fixes',
    summary: 'Improved clarity around dueling riposte mechanic.',
    content:
      "Players no longer see the NPC's attempt when performing a riposte in duel mode, making the mechanic clearer. Also, the same word can no longer occur twice in a row during duels.",
    tags: ['improvement', 'game'],
  },
  {
    id: 10,
    date: '2026-01-13',
    title: 'Learner login screen redesign',
    summary: 'A cleaner, more intuitive login screen for learners.',
    content:
      'The learner sign-in screen has been redesigned with a cleaner layout and clearer instructions, making it easier for learners to log in independently.',
    tags: ['improvement', 'game'],
  },
  {
    id: 9,
    date: '2026-01-08',
    title: 'Game performance optimisations',
    summary: 'Significant performance improvements across all game modes.',
    content:
      'We have made major performance improvements to the game engine, including faster asset loading, reduced memory usage, to optimise performance when multiple learners are playing at once.',
    tags: ['improvement', 'game'],
  },
  {
    id: 8,
    date: '2025-12-20',
    title: 'French word mat support',
    summary: 'Word mats can now be created in French.',
    content:
      'Teachers can now create French-language word mats, supporting bilingual and French immersion classrooms.',
    tags: ['feature'],
  },
  {
    id: 7,
    date: '2025-12-18',
    title: 'Word Stack game mode',
    summary: 'A brand new word-building game mode for learners.',
    content:
      'Word Stack is a new game mode where learners build words by stacking letters to better support word chains and spelling activities.',
    tags: ['feature', 'game'],
  },
  {
    id: 6,
    date: '2025-12-15',
    title: 'Customisable word mats',
    summary: 'Customise word mats with your own words and groupings.',
    content:
      'You can now fully customise word mats — add your own words, rearrange groups, and tailor the content to match your teaching sequence.',
    tags: ['feature'],
  },
  {
    id: 5,
    date: '2025-12-01',
    title: 'Household plan',
    summary: 'A new plan for families and home educators.',
    content:
      'We have introduced a Household plan, designed for parents and home educators who want to use Spellcaster with their children at home.',
    tags: ['feature'],
  },
  {
    id: 3,
    date: '2025-11-10',
    title: 'Morph Master minigame',
    summary: 'A new minigame focused on morphemes and word building.',
    content:
      'Morph Master is a new minigame that challenges learners to break down and build words using prefixes, suffixes, and root words.',
    tags: ['feature', 'game'],
  },
  {
    id: 2,
    date: '2025-10-20',
    title: 'Assessment rework',
    summary: 'A completely redesigned assessment experience.',
    content:
      'Assessments have been rebuilt from the ground up with a clearer interface, more detailed results, and the ability to assign assessments to individual learners or groups.',
    tags: ['improvement'],
  },
  {
    id: 1,
    date: '2025-10-15',
    title: 'Learner passwords',
    summary:
      'Learners now require a password to sign in for improved security.',
    content: (
      <div className="flex flex-col gap-2">
        <span>
          From Monday, November 3, learners will need both a username and
          password to access Spellcaster (previously only a username was
          required).
        </span>
        <span>
          <b>What you need to know:</b>
        </span>
        <ul className="list-disc list-inside pl-3">
          <li>
            We will automatically generate passwords for all current learners.
            Passwords will be auto-generated for any new learners you add.
          </li>
          <li>
            You will be able to view and edit passwords by navigating to a
            learner's settings in your dashboard.
          </li>
          <li>
            You will have access to learner passwords from October 27, learners
            will require them to login from November 3.
          </li>
        </ul>
        <span>
          <b>Tip:</b> You can print login cards for easy distribution to
          learners.{' '}
          <a
            target="_blank"
            href="https://spellcaster.education/documentation/managing-students/getting-learners-logged-in#exporting-login-details"
            className="hover:underline cursor-pointer text-primary"
            rel="noreferrer"
          >
            Click here to learn how to create printable login cards
          </a>
        </span>
        <img
          src="/misc/exportPassword.gif"
          alt="Viewing and exporting learner passwords"
          className="my-2 h-[300px] object-contain w-fit mx-auto rounded-sm"
        />
      </div>
    ),
    tags: ['feature'],
  },
];

export const tagStyles: Record<string, string> = {
  feature: 'bg-blue-100 text-blue-800 border-blue-200',
  improvement: 'bg-purple-100 text-purple-800 border-purple-200',
  fix: 'bg-amber-100 text-amber-800 border-amber-200',
  game: 'bg-green-100 text-green-800 border-green-200',
};

export const tagLabels: Record<string, string> = {
  feature: 'Feature',
  improvement: 'Improvement',
  fix: 'Fix',
  game: 'Game',
};

export default changelogItems;
